import { ref, computed } from 'vue';
import store from '@/store';
import { config } from '@/config';
import { t } from '@/utils/i18n-helper';

// Códigos de error que el backend devuelve en `errors` bajo las claves
// licensor_entitlement_id / buyer_id. No son frases legibles para el usuario
// final: se traducen a texto. Un código desconocido (no presente en el mapa)
// cae al mensaje genérico, nunca se muestra crudo.
const ENTITLEMENT_ERROR_MESSAGES = {
    ENTITLEMENT_REQUIRED_FOR_PRIVATE_SALE: 'Esta membresía requiere un entitlement de venta privada.',
    ENTITLEMENT_NOT_FOUND: 'No se ha encontrado el entitlement necesario para esta compra.',
    ENTITLEMENT_LIMIT_REACHED: 'Has alcanzado el límite de membresías permitido para esta compra.',
    ENTITLEMENT_REQUIRED_OUTSIDE_PRIVATE_SALE: 'Esta membresía requiere un entitlement para poder adquirirla.',
    ERROR_FETCHING_ENTITLEMENTS: 'No se pudieron cargar tus entitlements. Inténtalo de nuevo.',
    BUYER_ID_REQUIRED_FOR_ENTITLEMENT: 'No se pudo completar la compra. Inténtalo de nuevo.'
};
const ENTITLEMENT_ERROR_GENERICO = 'No se pudo completar la compra. Inténtalo de nuevo.';

/**
 * useAltaSimpatizanteSga
 *
 * Encapsula el estado y las llamadas API del alta de membresía de simpatizante
 * SGA en un wizard de 3 pasos:
 *   1. datos de comprador + precio seleccionado → POST sga.cart.membresia
 *   2. descuentos (auto + cupón) + condiciones aceptadas
 *   3. pago (Paycomet / formulario Redsys según `tipo_tpv`)
 *
 * La vista fina solo lee estas refs y llama a estos métodos; nunca cachea
 * copia local de los precios ni del estado de pago.
 *
 * @returns {Object} refs y métodos del alta de membresía.
 */
export function useAltaSimpatizanteSga() {
    // Si el usuario llegó a un paso intermedio en una sesión previa, ese
    // cartId quedó persistido. Lo descartamos siempre al montar: el alta
    // SIEMPRE empieza en paso 1. Si el usuario abandona un proceso a
    // medias, el cartId persistido queda inútil (el alta se reintentará
    // desde paso 1) y lo limpiamos en el siguiente reinicio.
    const persistedCartId = localStorage.getItem('sga_simp_cart_id');
    if (persistedCartId) {
        localStorage.removeItem('sga_simp_cart_id');
    }
    // Limpieza legacy: nunca más persistimos datos del comprador (PII) en
    // localStorage; si una versión anterior dejó la clave huérfana, la
    // eliminamos (no depende de que exista el cartId).
    localStorage.removeItem('sga_simp_comprador');

    // ── State ────────────────────────────────────────────────────────────────
    const loading = ref(false);
    const precios = ref([]);
    // datosComprador se mantiene null al montar aunque hubiera persistido —
    // el alta empieza de cero en paso 1.
    const datosComprador = ref(null);
    const error = ref(null);
    const precioSeleccionado = ref(null);
    const jetId = ref(null);
    const formularioPago = ref(null);
    const sessionId = ref(null);
    const cartId = ref(null);
    const totalPrice = ref(null);
    const tipoTpv = ref(null);
    const terminos = ref({ privacidad: false, condiciones: false, comunicaciones: false });
    const paso = ref(1);
    const descuentosAuto = ref([]);
    const cuponVerificado = ref(null);
    const cartItemId = ref(null);
    // Guard de operación de cupón: evita aplicar/quitar concurrente con la
    // misma fuente de verdad (backend confirmado antes de tocar la UI).
    const applyingCupon = ref(false);

    // Promesa de borrado de carrito en curso: eliminarCarrito la comparte
    // entre llamadas (Atrás/Cancelar dobles) para que el segundo llamador
    // reciba la misma operación y no se repita el DELETE.
    let deleteInFlight = null;

    // ── Computed ─────────────────────────────────────────────────────────────
    const hayFormulario = computed(() => !!formularioPago.value);
    const hayJetId = computed(() => !!jetId.value);
    const preciosDisponibles = computed(() => precios.value.filter(p => p.onSale));
    const preciosCerrados = computed(() => precios.value.filter(p => !p.onSale));
    const puedeEnviar = computed(() => {
        if (loading.value) return false;
        if (!precioSeleccionado.value) return false;
        if (!datosComprador.value) return false;
        if (!terminos.value.privacidad || !terminos.value.condiciones) return false;
        const v = validarComprador(datosComprador.value);
        return v.ok;
    });

    // Precio completo del objeto seleccionado. `precioSeleccionado` guarda el
    // salesCategoryId elegido en el paso 1; aquí resolvemos el objeto precio
    // completo desde `precios` para el desglose del paso 2 (resumen).
    const precioSeleccionadoInfo = computed(() => {
        const p = precios.value.find(p => p.salesCategoryId === precioSeleccionado.value)
            || precios.value.find(p => p.onSale)
            || precios.value[0]
            || {};
        return {
            salesCategoryName: p.salesCategoryName || '',
            amount: p.amount || 0,
            price_management: p.price_management || 0,
            price_management_interno: p.price_management_interno || 0,
            iva: 21,
        };
    });

    // Descuentos aplicables al desglose: los automáticos (normalizados) + el
    // cupón verificado, si existe.
    const descuentosAplicados = computed(() => {
        const list = [];
        (descuentosAuto.value || []).forEach(d => { if (d) list.push(d); });
        if (cuponVerificado.value) list.push(cuponVerificado.value);
        return list;
    });

    // Aplica un descuento (PERCENTAGE | FIXED) sobre un importe base.
    function aplicarDescuento(base, descuento) {
        if (!descuento) return base;
        const value = descuento.value ?? descuento.percentage ?? 0;
        const type = descuento.valueType
            || (typeof descuento.percentage === 'number' ? 'PERCENTAGE' : 'FIXED');
        if (type === 'PERCENTAGE') {
            return base - base * value / 100;
        }
        return Math.max(0, base - value);
    }

    // Subtotal (precio del producto menos todos los descuentos).
    const importeSinGastos = computed(() => {
        return descuentosAplicados.value.reduce(aplicarDescuento, precioSeleccionadoInfo.value.amount);
    });

    // Total a pagar. Cuando el backend ya devolvió `totalPrice` (tras crear
    // el carrito o refrescar el cobro), ese valor es la fuente de verdad:
    // incluye redondeos y reglas de gestión del servidor. El cálculo local
    // solo aplica en el estado previo a la primera respuesta.
    const totalPagar = computed(() => {
        if (totalPrice.value != null) {
            return totalPrice.value;
        }
        const info = precioSeleccionadoInfo.value;
        const gastos = info.price_management_interno ? 0 : info.price_management;
        return importeSinGastos.value + gastos;
    });

    // ── Helpers ──────────────────────────────────────────────────────────────
    function resolverErrorInfo(err) {
        // El backend puede traer su propio mensaje en err.response.data.message.
        // Si no, caemos al mensaje genérico del flujo SGA checkout.
        return err?.response?.data?.message
            || t('front.sga.checkout.error_derechos');
    }

    /**
     * Traduce un mensaje de `errors` del backend según la clave del campo.
     * Los errores de licensor_entitlement_id / buyer_id son códigos en
     * mayúsculas, no frases legibles: se mapean a texto de usuario y un
     * código desconocido cae al mensaje genérico (nunca se muestra crudo).
     * Cualquier otro campo (validaciones de comprador, categoryQuantity,
     * etc.) ya está redactado para mostrarse tal cual.
     */
    function traducirMensajeError(campo, mensaje) {
        if (campo === 'licensor_entitlement_id' || campo === 'buyer_id') {
            return ENTITLEMENT_ERROR_MESSAGES[mensaje] || ENTITLEMENT_ERROR_GENERICO;
        }
        return mensaje;
    }

    /**
     * Extrae los mensajes de validación de un 422 de creación de carrito de
     * membresía. `errors` es un objeto { campo: [mensajes...] }; se aplanan
     * todos los arrays. Si `errors` viene vacío o ausente devuelve null y el
     * llamador cae al mensaje global.
     */
    function resolverErrorsValidacion(err) {
        const errors = err?.response?.data?.errors;
        if (!errors || typeof errors !== 'object') return null;
        const mensajes = Object.entries(errors)
            .flatMap(([campo, lista]) => {
                const items = Array.isArray(lista) ? lista : [lista];
                return items.map(mensaje => traducirMensajeError(campo, mensaje));
            })
            .filter(Boolean);
        return mensajes.length ? mensajes.join('\n') : null;
    }

    function resolverErrorAlta(err) {
        // 422 de validación: los mensajes útiles están en `errors`, no en el
        // message global ("Error de validación"). Se muestran aplanados.
        return resolverErrorsValidacion(err)
            || err?.response?.data?.message
            || t('front.sga.checkout.error_guardar_datos');
    }

    // ── Methods ──────────────────────────────────────────────────────────────
    /**
     * Consulta GET /lfp/front/sga/cart/membresia/info y expone productId/prices.
     * Header Hash-Teatro se inyecta automáticamente en api.js (sin código extra).
     */
    async function fetchInfo() {
        loading.value = true;
        error.value = null;
        try {
            const response = await store.dispatch(config.getVuexAlias('sga.cart.membresia.info'));
            const data = response?.data ?? response;
            precios.value = data.prices ?? [];
        } catch (err) {
            error.value = resolverErrorInfo(err);
            console.error('[useAltaSimpatizanteSga] fetchInfo failed:', err);
        } finally {
            loading.value = false;
        }
    }

    /**
     * Regex de validación de documentos de identidad españoles.
     * - DNI: 8 dígitos + letra. Se valida la letra contra el algoritmo oficial
     *   (tabla DNI). Sin validación de letra, cualquier 8 dígitos pasa.
     * - NIE: X/Y/Z + 7 dígitos + letra. Se transforma X→0, Y→1, Z→2 y se
     *   calcula la letra como en DNI.
     * - PASAPORTE: alfanumérico, 6-12 caracteres (regla laxa; el backend
     *   hace la validación dura).
     * - NIF: persona jurídica. El propio backend gestiona el formato.
     */
    const DNI_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE';
    function validarDocumentoIdentidad(type, value) {
        const v = String(value || '').trim().toUpperCase();
        if (!v) return false;
        if (type === 'DNI') {
            if (!/^\d{8}[A-Z]$/.test(v)) return false;
            const num = parseInt(v.slice(0, 8), 10);
            return DNI_LETTERS[num % 23] === v.slice(8);
        }
        if (type === 'NIE') {
            if (!/^[XYZ]\d{7}[A-Z]$/.test(v)) return false;
            const first = { X: '0', Y: '1', Z: '2' }[v[0]];
            const num = parseInt(first + v.slice(1, 8), 10);
            return DNI_LETTERS[num % 23] === v.slice(8);
        }
        if (type === 'PASAPORTE') {
            return /^[A-Z0-9]{6,12}$/.test(v);
        }
        // NIF: el backend decide. Aceptamos cualquier string no vacío.
        return true;
    }

    /**
     * Valida los datos del comprador. lastName es opcional cuando el tipo de
     * documento es NIF (persona jurídica sin apellido). Para DNI/NIE valida
     * formato y letra del documento.
     *
     * @param {Object} comprador - Estructura {name,lastName,email,phone,identification{type,value,issuerCode},cp}
     * @returns {{ ok: boolean, errors: Object }}
     */
    function validarComprador(comprador) {
        const errors = {};
        // Reutilizamos `front.sga.checkout.error_campo_invalido` para todos los
        // mensajes de validación cliente. La clave ya tiene placeholder {campo}.
        const invalido = campo => t('front.sga.checkout.error_campo_invalido', { campo });
        if (!comprador?.name || !String(comprador.name).trim()) {
            errors.name = invalido('name');
        }
        if (!comprador?.email || !comprador.email.trim()) {
            errors.email = invalido('email');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(comprador.email)) {
            errors.email = invalido('email');
        }
        const tipoDoc = comprador?.identification?.type;
        const esNIF = tipoDoc === 'NIF';
        if (!esNIF && !comprador?.lastName) {
            errors.lastName = invalido('lastName');
        }
        if (!comprador?.identification?.value || !String(comprador.identification.value).trim()) {
            errors.identification = invalido('identification');
        } else if (!esNIF && !validarDocumentoIdentidad(tipoDoc, comprador.identification.value)) {
            errors.identification = invalido('identification');
        }
        return { ok: Object.keys(errors).length === 0, errors };
    }

    /**
     * Calcula isMinor a partir de una fecha de nacimiento ISO (YYYY-MM-DD).
     * Devuelve true si la fecha es válida y la persona tiene menos de 18 años.
     */
    function calcularEsMenor(birthdate) {
        if (!birthdate) return false;
        const birth = new Date(birthdate);
        if (isNaN(birth.getTime())) return false;
        const today = new Date();
        const age = today.getFullYear() - birth.getFullYear()
            - (today.getMonth() < birth.getMonth()
                || (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate()) ? 1 : 0);
        return age < 18;
    }

    /**
     * Mapea el shape extendido del comprador (incluye gender, birthdate, address,
     * email_confirm) al payload que el backend espera + las extensiones que
     * probablemente ignore. Estructura del payload basada en el flujo de
     * altas nuevas de abonos SGA (DatosAbonoSGAComponent + ProcesoCompraAbonadosSga).
     */
    function mapearCompradorParaEnvio(comprador) {
        const tipoDoc = comprador?.identification?.type;
        const esNIF = tipoDoc === 'NIF';
        return {
            // Spec original del backend
            name: comprador?.name || '',
            lastName: esNIF ? undefined : comprador?.lastName,
            email: comprador?.email || '',
            ...(comprador?.phone ? { phone: comprador.phone } : {}),
            identification: {
                type: tipoDoc || '',
                value: comprador?.identification?.value || '',
                issuerCode: 'ES'
            },
            hasNotOwnIdentification: false,
            isMinor: calcularEsMenor(comprador?.birthdate),
            cp: comprador?.cp || '',
            // Extensiones: el backend las ignora si no las espera
            ...(esNIF ? {} : { gender: comprador?.gender || '' }),
            ...(esNIF ? {} : { birthdate: comprador?.birthdate || '' }),
            email_confirm: comprador?.email_confirm || '',
            address: {
                country: comprador?.address?.country || '',
                // Cuando el país no tiene provincias (state/city vacíos),
                // no se envían esos campos al backend.
                ...(comprador?.address?.state ? { state: comprador.address.state } : {}),
                ...(comprador?.address?.city ? { city: comprador.address.city } : {}),
                zipCode: comprador?.cp || '',
                streetLine1: comprador?.address?.streetLine1 || ''
            }
        };
    }

    /**
     * Paso 1: envía POST /lfp/front/sga/cart/membresia SIN flags de condiciones.
     * Las condiciones se aceptan en el paso 2 vía `guardarCondiciones`.
     *
     * Persiste `cart_id` en localStorage para sobrevivir a F5 (edge case F.1).
     * Tras éxito avanza automáticamente a paso 2.
     *
     * @param {Object} compradorObj - Datos del comprador (shape extendido).
     * @param {String|Number} precioId - salesCategoryId del precio seleccionado.
     */
    async function enviarPaso1(compradorObj, precioId) {
        if (loading.value) return;
        datosComprador.value = compradorObj;
        precioSeleccionado.value = precioId;
        loading.value = true;
        error.value = null;

        const payload = {
            categoryQuantity: { salesCategoryId: precioId, quantity: 1 },
            comprador: mapearCompradorParaEnvio(compradorObj),
            fbp: window.localStorage.getItem('fbp') || null,
            fbc: window.localStorage.getItem('fbc') || null
        };

        try {
            const response = await store.dispatch(config.getVuexAlias('sga.cart.membresia'), payload);
            const data = response?.data ?? response;
            // Sin cart_id no hay carrito que gestionar en los pasos 2/3:
            // respuesta inválida. No avanzar y mostrar error accionable.
            if (!data.cart_id) {
                const err = new Error('enviarPaso1 sin cart_id en la respuesta');
                error.value = resolverErrorAlta(err);
                console.error('[useAltaSimpatizanteSga] enviarPaso1 failed:', err);
                throw err;
            }
            cartId.value = data.cart_id;
            localStorage.setItem('sga_simp_cart_id', data.cart_id);
            if (data.cart_item_id) cartItemId.value = data.cart_item_id;
            if (data.session_id) sessionId.value = data.session_id;
            if (data.jet_id) jetId.value = data.jet_id;
            if (data.formulario) formularioPago.value = data.formulario;
            if (data.total_price != null) totalPrice.value = data.total_price;
            if (data.tipo_tpv) tipoTpv.value = data.tipo_tpv;

            // Cargar los descuentos automáticos ANTES de publicar el paso 2:
            // la transición de paso y la disponibilidad de sus datos deben
            // ser atómicas (el resumen no enseña un precio incompleto ni
            // permite continuar con descuentos sin confirmar).
            await autoCargarDescuentos();

            paso.value = 2;
            return response;
        } catch (err) {
            error.value = resolverErrorAlta(err);
            console.error('[useAltaSimpatizanteSga] enviarPaso1 failed:', err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Paso 2: carga los descuentos automáticos que el backend asocia al carrito.
     * Llamado desde el orquestador tras `enviarPaso1` para pintar la lista de
     * promociones aplicables.
     */
    async function autoCargarDescuentos() {
        if (!cartId.value) return [];
        try {
            const res = await store.dispatch(
                config.getVuexAlias('sga.cart.get.descuentos'),
                { cart_id: cartId.value }
            );
            const data = res?.data?.data ?? res?.data;
            // Validación de shape: el backend agrupa por item
            //   [{ cart_item_sga_id, discounts: [...] }].
            // Un 2xx con payload inválido (objeto de error, forma inesperada)
            // NO se convierte en colección vacía: se marca error y se relanza.
            // Si aplicarCupon enviara esa colección incompleta a
            // guardar-descuentos, el backend reemplazaría la colección del
            // item y borraría descuentos automáticos legítimos.
            if (!Array.isArray(data)) {
                const err = new Error('Respuesta de descuentos con formato inválido');
                error.value = resolverErrorInfo(err);
                throw err;
            }
            descuentosAuto.value = data
                .flatMap(entry => (entry?.discounts && Array.isArray(entry.discounts) ? entry.discounts : []))
                .filter(Boolean);
            // El alta crea exactamente un item de carrito; su id real es el
            // `cart_item_sga_id` que el backend devuelve agrupando los
            // descuentos. Sin él, `guardar-descuentos` rechaza el payload
            // ("El campo items.0.id seleccionado no existe").
            const entry = data.find(e => e?.cart_item_sga_id != null);
            if (entry) cartItemId.value = entry.cart_item_sga_id;
            return descuentosAuto.value;
        } catch (err) {
            // Fail Fast: un fallo de carga de descuentos NO debe convertirse
            // en una colección vacía. Si aplicarCupon enviara solo el cupón a
            // guardar-descuentos, el backend reemplazaría los descuentos
            // automáticos legítimos. Se marca el error y se relanza para que
            // la vista lo muestre y el usuario pueda reintentar.
            console.error('[useAltaSimpatizanteSga] autoCargarDescuentos failed:', err);
            descuentosAuto.value = [];
            error.value = resolverErrorInfo(err);
            throw err;
        }
    }

    // Guard de verificación de cupón: dos clics rápidos pueden enviar códigos
    // distintos y confirmar el descuento/método de pago en un orden distinto
    // al de la intención del usuario.
    const verifyingCupon = ref(false);

    /**
     * Paso 2: verifica un código de cupón contra el backend. Devuelve el
     * descriptor del descuento (con `id`, `value`, etc.) si es válido, o null.
     */
    async function verificarCupon(codigo) {
        if (verifyingCupon.value) return null;
        if (!cartId.value) return null;
        verifyingCupon.value = true;
        try {
            const res = await store.dispatch(
                config.getVuexAlias('sga.cart.descuentos-codigo'),
                { filters: { codigo }, cart_id: cartId.value }
            );
            const data = res?.data?.data || res?.data || null;
            // NO se toca cuponVerificado aquí: verificar solo valida el
            // descriptor. aplicarCupon (que confirma guardar-descuentos en
            // backend) es el único que actualiza el estado de cupón.
            return data;
        } catch (err) {
            console.error('[useAltaSimpatizanteSga] verificarCupon failed:', err);
            return null;
        } finally {
            verifyingCupon.value = false;
        }
    }

    /**
     * Paso 2: aplica el descuento (cupón verificado) al carrito y refresca el
     * jet / formulario llamando a `solicitarCobro`. Solo marca el cupón como
     * verificado si el backend confirma el guardado; si no, revierte el estado
     * y relanza para que la vista muestre el error.
     */
    async function aplicarCupon(discount) {
        if (!cartId.value) return;
        if (applyingCupon.value) return; // guard: no operaciones concurrentes

        applyingCupon.value = true;
        try {
            // El id real del item viene del GET de descuentos (cart_item_sga_id).
            // Si aún no se cargó (p.ej. F5 en el paso 2), lo recuperamos antes de
            // guardar; sin él el endpoint rechaza el payload.
            if (cartItemId.value == null) {
                await autoCargarDescuentos();
            }
            // El backend REEMPLAZA la colección `discounts` del item: si solo
            // enviamos el cupón, se pierden los descuentos automáticos. Se
            // envía la colección completa (automáticos + cupón).
            await store.dispatch(
                config.getVuexAlias('sga.cart.descuentos-codigo.guardar'),
                {
                    cart_id: cartId.value,
                    items: [{ id: cartItemId.value, discounts: [...descuentosAuto.value, discount] }]
                }
            );
            // Confirmado en backend: ahora sí actualizamos el estado local.
            cuponVerificado.value = discount;
            await solicitarCobro();
        } catch (err) {
            // Revertir: la UI no debe mantener un cupón que el carrito no tiene.
            if (cuponVerificado.value?.id === discount?.id) {
                cuponVerificado.value = null;
            }
            throw err;
        } finally {
            applyingCupon.value = false;
        }
    }

    /**
     * Paso 2: retira el cupón del carrito y refresca el precio/cobro.
     * Solo limpia el estado local tras confirmar el guardado en backend.
     */
    async function quitarCupon() {
        if (!cartId.value) return;
        if (applyingCupon.value) return; // guard: no operaciones concurrentes

        applyingCupon.value = true;
        try {
            if (cartItemId.value == null) {
                await autoCargarDescuentos();
            }
            // Mismo criterio que aplicarCupon: se envía la colección completa
            // (solo los automáticos) para no borrarlos al quitar el cupón.
            await store.dispatch(
                config.getVuexAlias('sga.cart.descuentos-codigo.guardar'),
                {
                    cart_id: cartId.value,
                    items: [{ id: cartItemId.value, discounts: [...descuentosAuto.value] }]
                }
            );
            cuponVerificado.value = null;
            await solicitarCobro();
        } finally {
            applyingCupon.value = false;
        }
    }
    /**
     * Paso 2: persiste el resultado de los checkboxes legales (privacidad,
     * condiciones, comunicaciones) en el carrito.
     */
    async function guardarCondiciones({ privacidad, condiciones, comunicaciones }) {
        if (!cartId.value) return;
        await store.dispatch(
            config.getVuexAlias('sga.cart.guardar-condiciones'),
            {
                filters: {},
                cart_id: cartId.value,
                privacidad: !!privacidad,
                condiciones: !!condiciones,
                comunicaciones: !!comunicaciones
            }
        );
    }

    /**
     * Refresca el jet / formulario de pago desde el backend. Llamado tras
     * aplicar cupón y al pasar a paso 3. Lanza si el backend no devuelve un
     * medio de pago: la vista no debe avanzar a un paso 3 vacío.
     */
    async function solicitarCobro() {
        // Sin carrito no hay cobro posible: lanzar en vez de retornar en
        // silencio (la vista no debe avanzar a un paso 3 vacío).
        if (!cartId.value) {
            throw new Error('solicitarCobro sin carrito');
        }
        const res = await store.dispatch(
            config.getVuexAlias('sga.cart.solicitar-cobro'),
            { cart_id: cartId.value }
        );
        const data = res?.data ?? res;
        // Reemplazo atómico: cada respuesta define el método de pago actual.
        // Si el backend cambia de Redsys a Paycomet (o viceversa), el método
        // anterior no debe sobrevivir (PagoSimpatizanteSgaComponent da
        // prioridad al formulario Redsys y enviaría uno obsoleto).
        jetId.value = data.jet_id || null;
        formularioPago.value = data.formulario || null;
        // Un total de 0 (bonificación total) es un valor de negocio válido:
        // se comprueba != null, no truthy, para no conservar un valor previo.
        if (data.total_price != null) totalPrice.value = data.total_price;
        if (data.cart_item_id) cartItemId.value = data.cart_item_id;
        // Sin jet ni formulario no hay forma de pagar: es un contrato roto
        // del backend, la vista no debe avanzar al paso 3.
        if (!data.jet_id && !data.formulario) {
            throw new Error('solicitarCobro sin jet_id ni formulario');
        }
    }

    /**
     * Navega entre pasos. Avanza como máximo +1 hacia adelante (no se puede
     * saltar pasos), retrocede libremente.
     */
    function irAPaso(n) {
        if (typeof n !== 'number' || n < 1) return;
        if (n > paso.value) {
            paso.value = Math.min(n, paso.value + 1);
        } else {
            paso.value = n;
        }
    }

    /**
     * Resetea todas las refs del flujo a sus valores iniciales y limpia el
     * cartId persistido en localStorage. Single source of truth para el
     * reset — tanto `eliminarCarrito` como `cancelarYReiniciar` lo invocan.
     */
    function resetLocalState() {
        paso.value = 1;
        cartId.value = null;
        descuentosAuto.value = [];
        cuponVerificado.value = null;
        cartItemId.value = null;
        jetId.value = null;
        formularioPago.value = null;
        sessionId.value = null;
        totalPrice.value = null;
        datosComprador.value = null;
        precioSeleccionado.value = null;
        terminos.value = { privacidad: false, condiciones: false, comunicaciones: false };
        error.value = null;
        loading.value = false;
        localStorage.removeItem('sga_simp_cart_id');
    }

    /**
     * Limpia el carrito en el backend y resetea el estado local a paso 1.
     * Usado por el botón "Cancelar" del orquestador y por errores fatales.
     * Relanza si el borrado falla (red/5xx): la vista pinta el error y el
     * carrito sigue activo en backend, con botón disponible para reintentar.
     */
    async function cancelarYReiniciar() {
        await eliminarCarrito();
    }

    /**
     * DELETE /lfp/front/sga/cart/{cart_id}/borrar — descarta el carrito creado
     * en el alta y limpia el estado de pago local. Si no hay cart_id, solo
     * resetea local (idempotente con cancelaciones previas).
     *
     * Un 404 se trata como idempotente (el carrito ya no existe): se limpia
     * el estado local y no se bloquea la cancelación. Cualquier otro error
     * (red, 5xx) SE PROPAGA: el carrito sigue activo y el usuario no debe
     * creer que canceló.
     */
    function eliminarCarrito() {
        // Atrás/Cancelar pueden dispararse dobles: si ya hay un borrado en
        // curso se devuelve la misma promesa y no se repite el DELETE. El
        // resultado se comparte vía deleteInFlight hasta que resuelve.
        if (deleteInFlight) {
            return deleteInFlight;
        }

        const id = cartId.value;
        if (id) {
            deleteInFlight = store.dispatch(
                config.getVuexAlias('sga.cart.eliminar-datos'),
                { cart_id: id }
            )
                .then(() => {
                    resetLocalState();
                })
                .catch(err => {
                    if (err?.response?.status === 404) {
                        // Carrito ya eliminado en backend (cancelación previa o
                        // expiración): cancelación idempotente, limpiar local.
                        console.warn('[useAltaSimpatizanteSga] eliminarCarrito: carrito ya no existe', id);
                        resetLocalState();
                    } else {
                        // Fallo real de borrado: no resetear el estado local.
                        // El carrito sigue activo y el usuario debe poder
                        // reintentar la cancelación.
                        throw err;
                    }
                })
                .finally(() => {
                    deleteInFlight = null;
                });
            return deleteInFlight;
        }

        resetLocalState();
        return Promise.resolve();
    }

    return {
        // estado existente
        loading,
        precios,
        preciosDisponibles,
        preciosCerrados,
        hayFormulario,
        hayJetId,
        datosComprador,
        error,
        precioSeleccionado,
        jetId,
        formularioPago,
        sessionId,
        cartId,
        totalPrice,
        tipoTpv,
        verifyingCupon,
        // estado nuevo
        paso,
        descuentosAuto,
        cuponVerificado,
        applyingCupon,
        terminos,
        precioSeleccionadoInfo,
        descuentosAplicados,
        importeSinGastos,
        totalPagar,
        // métodos existentes
        fetchInfo,
        validarComprador,
        validarDocumentoIdentidad,
        calcularEsMenor,
        // métodos nuevos
        enviarPaso1,
        autoCargarDescuentos,
        verificarCupon,
        aplicarCupon,
        quitarCupon,
        guardarCondiciones,
        solicitarCobro,
        irAPaso,
        cancelarYReiniciar,
        eliminarCarrito
    };
}